import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../shared/ui/Modal";
import { submitOrderComplaint } from "../api";

// onSubmitted(content) is optional: lets the page show the new complaint without reloading.
const ComplaintSubmitModal = ({ orderNumber, isOpen, onClose, onSubmitted }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [complain, setComplain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset all input fields
  const resetModalFields = () => {
    setName("");
    setEmail("");
    setPhone("");
    setComplain("");
    setError("");
  };

  // Handle complaint submission
  const submitComplain = async (e) => {
    e.preventDefault();
    setError("");

    if (!name || !email || !phone || !complain.trim()) {
      setError("All fields are required.");
      return;
    }

    setIsLoading(true);
    try {
      await submitOrderComplaint({
        name,
        email,
        phoneNumber: phone,
        content: complain,
        trackingNumber: orderNumber,
      });

      toast.success("Complaint submitted successfully. We will get back to you soon.");
      onSubmitted?.(complain);
      resetModalFields();
      setTimeout(() => onClose(), 2000); // Auto-close modal after success
    } catch (err) {
      console.error("Error submitting complaint:", err);
      setError(err.response?.data || err.message || "An error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  // Close modal and reset fields
  const handleClose = () => {
    onClose();
    resetModalFields();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit a complaint"
      subtitle="Tell us what went wrong with this order and we'll get back to you."
      dismissible={!isLoading}
      footer={
        <>
          <button type="button" className="ui-btn ui-btn--ghost" onClick={handleClose} disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="complaint-form" className="ui-btn ui-btn--primary" disabled={isLoading}>
            {isLoading ? "Submitting..." : "Submit complaint"}
          </button>
        </>
      }
    >
      <form id="complaint-form" className="ui-form" onSubmit={submitComplain}>
        {error && (
          <p className="ui-note ui-note--error" role="alert">
            <i className="ph ph-warning-circle" />
            <span>{typeof error === "string" ? error : "An error occurred."}</span>
          </p>
        )}
        <div className="ui-row">
          <div className="ui-field">
            <label className="ui-label" htmlFor="complaint-name">Name</label>
            <input
              id="complaint-name"
              type="text"
              className="ui-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="complaint-phone">Phone</label>
            <input
              id="complaint-phone"
              type="tel"
              className="ui-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="complaint-email">Email</label>
            <input
              id="complaint-email"
              type="email"
              className="ui-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="complaint-tracking">Tracking number</label>
            <input
              id="complaint-tracking"
              type="text"
              className="ui-input"
              value={orderNumber || ""}
              disabled
            />
          </div>
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor="complaint-content">Your complaint</label>
          <textarea
            id="complaint-content"
            className="ui-textarea"
            rows="4"
            value={complain}
            onChange={(e) => setComplain(e.target.value)}
            required
          />
        </div>
      </form>
    </Modal>
  );
};

export default ComplaintSubmitModal;
